import React, { Fragment, FunctionComponent, useEffect, useMemo, useState } from 'react';

import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import TagManager from 'react-gtm-module';

import Text from 'common/components/Text/Text';
import { SessionState, setFilters, setProfile } from 'features/session/sessionSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useGetAllCountriesQuery,
  useGetAllSubjectsQuery,
  useAddProfileMutation,
} from 'services/exploreApi';
import { navigateTop, profileAnswers } from 'common/utils';
import { setPreferencesFilled, setLastStepDone } from 'features/user/userSlice';

import styles from './UserInformation.module.scss';
import { PreferencesImg } from 'assets/session/dashboard/index';
import { CommonHash } from 'app/types';
import { TUITION_RANGES } from 'common/constants';

const tuitionOptions = TUITION_RANGES.map(([k, min, max]) => ({
  label: k,
  value: [k, [min, max]],
}));

const preferencesAnswers = ['countries', 'subjects', 'budget', 'duration', 'attendance', 'mode'];

// const currentYear: number = new Date().getFullYear();
// const years = [];
// for (let i = currentYear; i <= currentYear + 10; i++) {
//   years.push(i);
// }
// const yearsOptions = years.map((v: number, i: number) => ({ label: v, value: v }));

// const monthsOptions = MONTHS.map((v: string, i: number) => ({ label: v, value: i + 1 }));

const PreferencesForm: FunctionComponent = () => {
  const { profile, filters, user } = useAppSelector(
    ({ session }: { session: SessionState }) => session
  );
  const [addProfile, { data: newProfile }] = useAddProfileMutation();
  const { data: countries } = useGetAllCountriesQuery();
  const { data: subjects } = useGetAllSubjectsQuery();
  const [answers, setAnswer] = useState<CommonHash>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onAnswerPush = (key: string) => (value: any) => {
    if (!value) return;
    setAnswer((a: any) => ({ ...a, [key]: [...new Set([...(a[key] || []), value])] }));
  };

  const onAnswerPop = (key: string) => (value: any) => {
    if (!value) return;
    setAnswer((a: any) => ({
      ...a,
      [key]: (a[key] || []).filter((v: any) => v !== value),
    }));
  };

  const isAnswersFull = (type: string) => {
    return answers[type]?.length == 3;
  };

  const setInputOption = (key: string, value: any) => {
    setAnswer((a: any) => ({ ...a, [key]: value }));
  };

  const cleanAnswers = () => {
    preferencesAnswers.map((key) => {
      if (key.includes('budget')) {
        if (answers['budget'] == null || answers['budget'][0] == 'Any') {
          answers['budget'] = answers['budget_min'] = answers['budget_max'] = null;
        } else {
          answers['budget_min'] = answers['budget'][1][0];
          answers['budget_max'] = answers['budget'][1][1];
          answers['budget'] = answers['budget'][0];
        }
      } else if (['subjects', 'countries'].includes(key)) {
        answers[key] ? null : (answers[key] = []);
      } else {
        answers[key] ? null : (answers[key] = null);
      }
    });
  };

  const updateFilters = () => {
    const newFilters: CommonHash = new Object();
    Object.keys(filters).map((k: string) => {
      k.includes('order') ? null : (newFilters[k] = filters[k]);
    });
    newFilters['order_by_scoring'] = 'desc';
    dispatch(setFilters(newFilters));
  };

  const checkPreferencesFilled = () => {
    let count = 0;
    Object.keys(answers).forEach((key) => {
      if (preferencesAnswers.includes(key)) {
        if (['countries', 'subjects'].includes(key)) {
          if (answers[key].length) count++;
        } else {
          count++;
        }
      }
    });
    if (count / preferencesAnswers.length === 1) {
      dispatch(setPreferencesFilled(true));
      dispatch(setLastStepDone(2));
    } else {
      dispatch(setPreferencesFilled(false));
    }
  };

  const createProfile = () => {
    setIsLoading(true);
    cleanAnswers();
    updateFilters();
    checkPreferencesFilled();
    profile ? addProfile(answers) : addProfile({ ...answers });
  };

  const sendGTMEvents = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: profile ? 'profiling_edit_see_results_basic' : 'profiling_see_results_basic',
      },
    });
    TagManager.dataLayer({
      dataLayer: { event: 'user_identified', userId: user?.id },
    });
  };

  useEffect(() => {
    setInputOption('email', user?.email);
    setInputOption('full_name', user?.name);
    setInputOption('phone_number', user?.phone);
    TagManager.dataLayer({
      dataLayer: { event: 'ga_event', eventName: profile ? 'profiling_edit' : 'profiling_start' },
    });
  }, []);

  useEffect(() => {
    if (newProfile) {
      dispatch(setProfile(newProfile.data));
      sendGTMEvents();
      navigate('/home');
      setTimeout(() => {
        navigateTop(0, 0, 'topics');
      }, 100);
    }
  }, [newProfile]);

  useMemo(() => {
    if (profile) {
      setAnswer(profileAnswers(profile));
    }
  }, [profile]);

  return (
    <div className={`${isMobile ? '' : 'container d-flex flex-row'}`}>
      <div className={styles.sectionContainer}>
        {isMobile ? (
          <div className={styles.header}>
            <div className={styles.prevStepIcon} onClick={() => navigate(-1)}>
              <i className="bi bi-chevron-left"></i>
            </div>
            <Text size={18} weight={700} color="primary">
              Preferences
            </Text>
            <img src={PreferencesImg} className={styles.image} style={{ right: '18px' }}></img>
          </div>
        ) : (
          <div style={{ marginBottom: '22px' }}>
            <Text size={16} weight={500} color="primary">
              Account {'>'} Preferences
            </Text>
            <div className={styles.desktopHeader}>
              <div>
                <Text size={24} weight={700} color="primary" inlineStyle={{ marginTop: '30px' }}>
                  Preferences
                </Text>
                <Text size={18} color="secondary" inlineStyle={{ marginTop: '10px' }}>
                  Fill in as much information to find out your best match program and university
                </Text>
              </div>
              <div>
                <button className={styles.saveButton} onClick={createProfile} disabled={isLoading}>
                  {isLoading ? (
                    <div className={`spinner-border ${styles.loading}`} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    'Save & Leave'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className={styles.stepContainer}>
          <div className={styles.inputsContainter}>
            <div className={styles.inputGroup} style={{ width: '100%' }}>
              <Text
                size={isMobile ? 16 : 18}
                weight={isMobile ? 500 : 600}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Preferred destination (max 3)
              </Text>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={styles.questionSelect}
                value={answers['countries']?.[answers.length - 1] || 'Search country'}
                options={countries?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                onChange={({ value }: any) => onAnswerPush('countries')(value)}
                isDisabled={isAnswersFull('countries')}
              />
              <div className={`${styles.questionAnswer} ${styles.questionAnswerTag}`}>
                {(answers['countries'] || []).map((cId: number) => (
                  <div
                    className={styles.answerTag}
                    key={cId}
                    onClick={() => onAnswerPop('countries')(cId)}
                  >
                    {countries?.data.find((c) => c.id === cId)?.name}
                    <i className="bi bi-x"></i>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.inputsContainter}>
            <div className={styles.inputGroup} style={{ width: '100%' }}>
              <Text
                size={isMobile ? 16 : 18}
                weight={isMobile ? 500 : 600}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Preferred subjects (max 3)
              </Text>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={styles.questionSelect}
                value={answers['subjects']?.[answers.length - 1] || 'Search subject'}
                options={subjects?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                onChange={({ value }: any) => onAnswerPush('subjects')(value)}
                isDisabled={isAnswersFull('subjects')}
              />
              <div className={`${styles.questionAnswer} ${styles.questionAnswerTag}`}>
                {(answers['subjects'] || []).map((cId: number) => (
                  <div
                    className={styles.answerTag}
                    key={cId}
                    onClick={() => onAnswerPop('subjects')(cId)}
                  >
                    {subjects?.data.find((c) => c.id === cId)?.name}
                    <i className="bi bi-x"></i>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.inputsContainter}>
            <div className={styles.inputGroup} style={{ marginBottom: '40px', width: '100%' }}>
              <Text
                size={isMobile ? 16 : 18}
                weight={isMobile ? 500 : 600}
                inlineStyle={{ marginBottom: '12px' }}
                color="primary"
              >
                Preferred budget
              </Text>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={styles.questionSelect}
                value={tuitionOptions.find(
                  (o: any) => o.label === (answers['budget']?.[0] || 'Any')
                )}
                options={tuitionOptions}
                onChange={({ value }: any) => setInputOption('budget', value)}
              />
            </div>
          </div>
          <div className={styles.inputsContainter}>
            <Text size={isMobile ? 16 : 18} weight={isMobile ? 500 : 600} color="primary">
              Study mode
            </Text>
            <div className={styles.typeProgramContainer}>
              <Text size={14} weight={500} inlineStyle={{ marginBottom: '12px' }} color="primary">
                Duration
              </Text>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['12 months', 12],
                  ['18 months', 18],
                  ['24 months', 24],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="duration"
                      id={`duration-${v}`}
                      checked={answers['duration'] === v}
                      onChange={() => setInputOption('duration', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`duration-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className={styles.typeProgramContainer}>
              <Text size={14} weight={500} inlineStyle={{ marginBottom: '12px' }} color="primary">
                Attendance
              </Text>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['Full-time', 'full_time'],
                  ['Part-time', 'part_time'],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="attendance"
                      id={`attendance-${v}`}
                      checked={answers['attendance'] === v}
                      onChange={() => setInputOption('attendance', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`attendance-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className={styles.typeProgramContainer}>
              <Text size={14} weight={500} inlineStyle={{ marginBottom: '12px' }} color="primary">
                Mode
              </Text>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['In person', 'in_person'],
                  ['Online', 'online'],
                  ['Blended', 'blended'],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="mode"
                      id={`mode-${v}`}
                      checked={answers['mode'] === v}
                      onChange={() => setInputOption('mode', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`mode-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
          {isMobile && (
            <>
              <button
                className={styles.nextStepButton}
                type="submit"
                onClick={createProfile}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className={`spinner-border ${styles.loading}`} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  'Finish'
                )}
              </button>
              <button className={styles.saveButton} onClick={createProfile} disabled={isLoading}>
                {isLoading ? (
                  <div className={`spinner-border ${styles.loading}`} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  'Save & Leave'
                )}
              </button>
            </>
          )}
        </div>
      </div>
      {!isMobile && <div className={styles.rightColumn}></div>}
    </div>
  );
};

export default PreferencesForm;
